// @flow

import * as React from 'react';
import { type InvalidationPredicateResponse, useInvalidatableQuery } from '@ta/platform.urql';
import { featureIsEnabled } from '@ta/common.features';
import { objectStatisticQuery, toSavesObject, type QueryObject } from '@ta/trips.trip-util';
import { type TripsContext, type TripInteractionSources } from '@ta/trips.tracking';
import * as Types from '@ta/trips.trip-types';
import TripFlowSelector from './TripFlowSelector';

type Props = {|
  /** The object to save to a trip. Assume it has a __typename and a corresponding id field.
   * TODO:  A unique ID identifier will be put into the schema which we can use later  */
  object?: any,
  /** Prevents interaction logging. Useful when the interaction has been logged upstream, such as in SaveAction on SocialBar */
  preventInteractionPropagation?: boolean,
  /** Context to use for tracking */
  trackingContext?: TripsContext,
  /** Source to use for tracking */
  trackingSource?: TripInteractionSources,
  /** Optional: The originating Trip if the item is being re-saved */
  trip?: Types.Trip,
  /** Optional: If automatically popping the STAT modal via URL hash is allowed */
  allowPopStatModal?: boolean,
  /** A render prop which can open the trip flow. */
  children: (openFlow: () => mixed, active: boolean) => React.Node,
|};

type InternalProps = {|
  ...Props,
  /** Resolved object to save to a trip.   */
  queryObject: QueryObject,
|};

/**
 * An uncontrolled component which wraps a TripFlowSelector with a graphQL query. The logic here is as follows:
 * 1) Check to see if the item flag should be enabled by checking the isSaved social statistic flag
 * 2) If that flag is not set, load the isSaved from graphql directly.
 */
const QueriedTripFlowSelector = ({
  object,
  queryObject,
  preventInteractionPropagation,
  trackingContext,
  trackingSource,
  children,
  trip,
  allowPopStatModal,
}: InternalProps) => {
  const [isActive, setActive] = React.useState(
    object?.object?.socialStatistics
      ? !!object?.object?.socialStatistics?.isSaved
      : !!object?.socialStatistics?.isSaved,
  );

  const invalidatePredicate = (
    id: ?string,
    type: ?Types.ReferenceType,
  ): InvalidationPredicateResponse => {
    if (!id || !type) {
      return 'invalidateAndRefetch';
    }
    if (type === queryObject.type && id === String(queryObject.id)) {
      return 'invalidateAndRefetch';
    }
    return 'validate';
  };

  const [{ fetching, data, error }] = useInvalidatableQuery(
    {
      query: queryObject.query,
      variables: queryObject.variables,
      executeWhen: object?.socialStatistics ? 'afterInvalidation' : 'immediately',
    },
    {
      addItemToTrip: ({ referenceId, referenceType }) =>
        invalidatePredicate(referenceId, referenceType),
      // reference variable is not a part of the mutation, but should be passed in for validation. if it's not, invalidate all saved statuses
      tripsRemoveItem: ({ reference }) => invalidatePredicate(reference?.id, reference?.type),
      tripsRemoveReference: ({ reference }) => invalidatePredicate(reference?.id, reference?.type),
    },
  );

  React.useEffect(() => {
    if (fetching || error) {
      return;
    }

    const obj = data && data.savesObjects && data.savesObjects[0];
    if (obj) {
      const { id, socialStatistics = {} } = obj;
      const active =
        id === queryObject.id || String(id) === queryObject.id ? !!socialStatistics.isSaved : false;
      if (active !== isActive) {
        setActive(active);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetching, data, error]);

  return (
    <TripFlowSelector
      object={object}
      active={isActive}
      preventInteractionPropagation={preventInteractionPropagation}
      trackingContext={trackingContext}
      trackingSource={trackingSource}
      resaveTrip={trip}
      allowPopStatModal={allowPopStatModal}
    >
      {openFlow => children(openFlow, isActive)}
    </TripFlowSelector>
  );
};

/**
 * Clean up main component code by extracting feature gate and validations into a separate wrapper component.
 */
const ValidatedQueriesTripFlowSelector = (props: Props) => {
  if (!featureIsEnabled('trips_bookmark')) {
    return null;
  }

  const saveObject = toSavesObject(props.object, true);
  if (!saveObject) {
    console.error(`An invalid saves object was passed to QueriedTripFlowSelector`);
    return null;
  }

  // If we don't have a 'saved' answer coming in, then ask graphql.
  const queryObject = objectStatisticQuery(saveObject);
  if (!queryObject) {
    console.error(`Unsupported saves object was passed to QueriedTripFlowSelector`);
    return null;
  }

  return <QueriedTripFlowSelector {...props} queryObject={queryObject} />;
};

ValidatedQueriesTripFlowSelector.defaultProps = {
  object: null,
  trackingContext: undefined,
  allowPopStatModal: false,
};

export default ValidatedQueriesTripFlowSelector;
